import type { ExperimentPayloadValue, PawsExperiments } from "./types";

/**
 * Payload-driven presentation levers for the quiz/results app.
 *
 * `useExperimentValue` reads one scalar value from the assigned variant's
 * payload (attached to `window.__PAWS_EXPERIMENTS__.assignments[key].payload`
 * by snippets/paws-experiments.liquid, always looked up fresh from the shop
 * config — never from localStorage). It is type-guarded: the payload value is
 * used only when its `typeof` matches the default's, otherwise the default
 * renders. Absent experiment, absent payload key, wrong type, bootstrap
 * failure, design mode — all fall back to the default (fail open).
 *
 * RULES (see docs/experiments.md):
 * - Defaults are MANDATORY and must equal the current production copy, so a
 *   missing/paused/completed experiment renders exactly what ships today.
 * - Presentation only. NO money values and NO calculations may ever come
 *   from a payload — prices, discounts and totals stay in application code.
 *
 * First lever experiment: `quiz-presentation-v1` (constant below). Levers
 * wired to it:
 *   offer_headline       — plan step offer subhead (ResultsStepPlan.tsx)
 *   plan_badge_text      — "Best value" badge label (plan/PlanCard.tsx)
 *   savings_label        — "Total Saving" label (ResultsReview.tsx)
 *   checkout_button_text — "Go to Checkout" button label (ResultsFooter.tsx)
 */

/** The one experiment key the quiz presentation levers are wired to. */
export const QUIZ_PRESENTATION_EXPERIMENT_KEY = "quiz-presentation-v1";

// Read once at module load, mirroring useExperiment.ts: the bootstrap runs
// synchronously in <head>, so the store exists (or never will) before React.
const store: Readonly<PawsExperiments> | null =
  typeof window !== "undefined" && window.__PAWS_EXPERIMENTS__
    ? window.__PAWS_EXPERIMENTS__
    : null;

export function useExperimentValue<T extends ExperimentPayloadValue>(
  experimentKey: string,
  valueKey: string,
  defaultValue: T
): T {
  const payload = store?.assignments?.[experimentKey]?.payload;
  if (!payload) return defaultValue;
  const value = (payload as Readonly<Record<string, unknown>>)[valueKey];
  return typeof value === typeof defaultValue ? (value as T) : defaultValue;
}
